import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DatePicker } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { ContentStateProperty } from '../../../../@types/types'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { useIsRequiredProperty } from '../../../../services/validation/requiredFieldsUtils/useIsRequiredProperty'
import { getNormalizedToday } from '../../../../utils/date-utils'
import styles from './Datepicker.module.css'

export interface DatepickerProps {
  id: string
  propertyName: ContentStateProperty
  defaultCancelBehaviour?: boolean
  placeholder?: string
  labelTranslationKey: string
  labelClass?: string
  selectedDate?: Date
  selectedEndDate?: Date
  minDate?: Date
  dateRange?: boolean
  errors: string[]
  onChange: (field: ContentStateProperty, startDate?: Date, endDate?: Date) => void
  onBlur?: (field: ContentStateProperty, isUnchanged: boolean, value?: Date) => void
}

const generateRandomValue = () => `datepicker-${Math.random()}${new Date().getTime()}`
const MemoizedDatePicker = React.memo(DatePicker)
const Datepicker: React.FC<DatepickerProps> = (props) => {
  const {
    id,
    propertyName,
    placeholder,
    labelTranslationKey,
    selectedDate,
    selectedEndDate,
    defaultCancelBehaviour,
    dateRange,
    minDate,
    errors,
    onChange,
    onBlur,
  } = props
  const [state, setState] = useState<{
    isUnchanged: boolean
    lastStartValue: Date | undefined
    lastEndValue: Date | undefined
  }>({
    isUnchanged: true,
    lastStartValue: selectedDate,
    lastEndValue: selectedEndDate,
  })
  const { setTouched, formState } = useFormState()
  const [selected, setSelected] = useState(() => generateRandomValue())
  const previousSelectionRef = useRef({
    selectedDateTimestamp: selectedDate?.getTime(),
    selectedEndDateTimestamp: selectedEndDate?.getTime(),
  })
  const { t } = useTranslation()
  const selectedDateTimestamp = selectedDate?.getTime()
  const selectedEndDateTimestamp = selectedEndDate?.getTime()

  useEffect(() => {
    const hasSelectionChanged =
      previousSelectionRef.current.selectedDateTimestamp !== selectedDateTimestamp ||
      previousSelectionRef.current.selectedEndDateTimestamp !== selectedEndDateTimestamp

    if (!hasSelectionChanged) {
      return
    }

    previousSelectionRef.current = {
      selectedDateTimestamp,
      selectedEndDateTimestamp,
    }
    setSelected(generateRandomValue())
  }, [selectedDateTimestamp, selectedEndDateTimestamp])

  const doChange = useCallback(
    (selectedStartDate?: Date, selectedEndDate?: Date) => {
      const newStartDate = selectedStartDate ? selectedStartDate : undefined
      const newEndDate = selectedEndDate ? selectedEndDate : undefined
      onChange(propertyName, newStartDate, newEndDate)
      setTouched(propertyName)
      if (newStartDate && state.lastStartValue?.getTime() !== newStartDate.getTime()) {
        onBlur && onBlur(propertyName, state.isUnchanged, newStartDate)
        setState((prev) => ({ ...prev, lastStartValue: new Date(newStartDate), isUnchanged: false }))
      }

      if (newEndDate && state.lastEndValue?.getTime() !== newEndDate.getTime()) {
        onBlur && onBlur(propertyName, state.isUnchanged, newEndDate)
        setState((prev) => ({ ...prev, lastEndValue: new Date(newEndDate), isUnchanged: false }))
      }
    },
    [onChange, propertyName, setTouched, state.lastStartValue, state.lastEndValue, state.isUnchanged, onBlur]
  )
  const onCancelCallback = useCallback(() => doChange(getNormalizedToday()), [doChange])

  let cancelProps = {}
  if (!defaultCancelBehaviour) {
    cancelProps = {
      cancelButtonText: t('button.today', 'Today'),
      onCancel: onCancelCallback,
    }
  }
  const showErrors = () => errors.length > 0 && (formState.submitCount > 0 || formState.touched[propertyName])
  const isMandatory = useIsRequiredProperty(propertyName)

  const currentDate = useMemo(() => new Date(), [])
  return (
    <div className={styles.datepicker}>
      <MemoizedDatePicker
        /*
         We set the key to random values whenever the user selected a date (or deletes it etc.) in order for the component to be recreated.
         Because cosma is using uncontrolled components right now, we are not able to set a value from the outside, thus the "hack"
        */
        key={selected}
        id={id}
        placeholder={placeholder}
        label={t(labelTranslationKey)}
        initialDisplayedMonth={currentDate}
        cancelButtonClassName={'button-secondary cancel-button'}
        success={selectedDate !== undefined && !showErrors()}
        preselectedDate={selectedDate}
        minDate={minDate}
        preselectedEndDate={selectedEndDate}
        daterange={dateRange}
        invalid={showErrors()}
        errorText={showErrors() ? t(`validation.${errors[0]}`) : undefined}
        enableDeletion={true}
        showIconRight='is24-icon-calendar'
        onChange={doChange}
        {...cancelProps}
        inputProps={{
          required: isMandatory,
        }}
      />
    </div>
  )
}

export default Datepicker
